import React, { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { ChevronDown, Globe, ArrowRight } from "lucide-react";
import { PageHero, Section, Container, Eyebrow, H2, Lead, CTAButton, GlassCard, Orbs } from "../components/PageUI";
import { coreModules, freeApps, comingSoon } from "../mock";
import SEO from "../components/SEO";

export default function Software() {
  return (
    <div>
      <SEO
        title="Free Masjid Software Features | Masjid Suite"
        description="Every Masjid Suite feature is free: donations, Zakat, membership, events, announcements, prayer times, multi-masjid management, and the Admin, Member, and Tap & Pay apps."
        path="/software"
      />
      <PageHero
        eyebrow="Features"
        title="Everything you need"
        gradient="to run your masjid, free forever."
        lead={<>No tiers, no trials, no “contact us for pricing.” Every feature below is included, for every masjid, at no cost.</>}
        cta={<CTAButton to="/contact">Get Started Free</CTAButton>}
      />

      <Section id="apps" className="bg-section-alt overflow-hidden">
        <Container>
          <div className="text-center mb-14">
            <H2 gradient="all free">Core Feature,</H2>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            {coreModules.map((m, i) => (<ModuleCard key={i} m={m} />))}
          </div>
        </Container>
      </Section>

      <Section id="free-apps" className="bg-section-alt overflow-hidden">
        <Container>
          <div className="text-center mb-14">
            <Eyebrow>Apps</Eyebrow>
            <H2 gradient="always free.">Free Apps,</H2>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            {freeApps.map((m, i) => (<ModuleCard key={i} m={m} linkAsButton />))}
          </div>
        </Container>
      </Section>

      <Section id="coming-soon" className="bg-hero-light overflow-hidden">
        <Orbs />
        <Container>
          <div className="text-center mb-14">
            <Eyebrow>Roadmap</Eyebrow>
            <H2 gradient="what's next.">Coming Soon,</H2>
            <div className="mt-6 flex justify-center"><Lead>We're always building. Here's a look at what's coming next to Masjid Suite.</Lead></div>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            {comingSoon.map((m, i) => (<ModuleCard key={i} m={m} />))}
          </div>
        </Container>
      </Section>

      <Section className="bg-section-alt overflow-hidden">
        <Orbs />
        <Container className="text-center">
          <div className="inline-flex items-center gap-2 rounded-full glass-frosted px-4 py-1.5 text-[#163410] text-[13px] font-medium mb-6"><Globe size={14}/> Multi-lingual</div>
          <H2 gradient="language">Speak your community’s</H2>
          <div className="mt-6 flex justify-center"><Lead>The full platform is available in multiple languages, so your volunteers and members can each use it comfortably in the language they’re most fluent in.</Lead></div>

          <div className="mt-16">
            <Eyebrow>Our Model</Eyebrow>
            <H2 gradient="per-feature pricing.">No subscription. No</H2>
            <div className="mt-6 flex justify-center"><Lead>Every module above is included from day one. There’s no “Basic” or “Professional” tier to choose between, and nothing extra to unlock later. You only ever pay Masjid Suite for physical hardware or a new website.</Lead></div>
            <div className="mt-10 flex justify-center"><CTAButton to="/why-us#how-we-stay-free" variant="secondary">Here’s how that’s possible</CTAButton></div>
          </div>
        </Container>
      </Section>

      <Section className="bg-section-alt overflow-hidden">
        <Container>
          <div className="glass-frosted rounded-[40px] p-10 md:p-16 text-center shadow-apple-lg">
            <H2 gradient="in a few days.">Get every module, free,</H2>
            <div className="mt-10 flex justify-center"><CTAButton to="/contact">Get Started Free</CTAButton></div>
          </div>
        </Container>
      </Section>
    </div>
  );
}

function ModuleCard({ m, linkAsButton = false }) {
  const [open, setOpen] = useState(false);
  const Icon = Icons[m.icon] || Icons.Sparkles;
  return (
    <GlassCard hover={false}>
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#1e4218] to-[#0e2408] text-white flex items-center justify-center shrink-0 shadow-md"><Icon size={22} strokeWidth={1.8} /></div>
        <div className="flex-1">
          <div className="flex items-center flex-wrap gap-2 mb-2">
            <h3 className="font-display text-xl text-[#1d1d1f] font-semibold">{m.title}</h3>
            {m.soon && <span className="text-[10px] uppercase tracking-widest font-semibold text-[#a8862f] bg-[#f6e8c6]/50 border border-[#c9a34a]/30 rounded-full px-2.5 py-0.5">Coming Soon</span>}
          </div>
          <p className="text-[14px] text-[#4b5563] leading-relaxed">{m.desc}</p>
          {m.link && (
            linkAsButton ? (
              <Link to={m.link} className="group mt-4 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-[13px] font-medium btn-primary">
                See more <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            ) : (
              <Link to={m.link} className="mt-3 inline-flex items-center gap-1 text-[#0e2408] text-[13px] font-medium hover:gap-2 transition-all">See more →</Link>
            )
          )}
          {m.detail && (
            <>
              <button onClick={() => setOpen(!open)} className="mt-3 inline-flex items-center gap-1 text-[#0e2408] text-[13px] font-medium">
                <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} /> {open ? "Show less" : "Show more"}
              </button>
              <motion.div initial={false} animate={{ height: open ? "auto" : 0, opacity: open ? 1 : 0 }} className="overflow-hidden">
                <p className="pt-3 text-[13px] text-[#4b5563] leading-relaxed">{m.detail}</p>
              </motion.div>
            </>
          )}
        </div>
      </div>
    </GlassCard>
  );
}
